import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { SelectType } from '@components/forms/offline-sales/select-type';
import { IconDownload } from '@components/icons';
import { SummaryFormModalContent, SummarySuccessModalContent } from '@components/modals';
import { SummaryFormMultiModalContent } from '@components/modals/summary-form-multi-modal';
import { Addon } from '@domain/addon';
import { Admission } from '@domain/admission';
import { Event } from '@domain/event';
import { AddGuestResponse } from '@hooks/use-add-guest/use-add-guest';
import { createBookingLink } from '@utils/create-booking-link';

import {
  OfflineSalesForm,
  ProcessOfflineSalesStep,
  SaleType,
  SINGLE_OFFLINE_SELL,
  STEP_INFO,
  STEP_SELECT_TYPE,
  STEP_SUCCESS,
} from '../process-offline-sales.type';

export const ProcessStepContent = ({
  step,
  offer,
  event,
  saleType,
  quantity,
  addons,
  addGuestData,
  addOns,
  email,
  closeModal,
  handleCloseModal,
  onSubmit,
  setStep,
}: {
  step: ProcessOfflineSalesStep;
  offer?: Admission;
  event?: Event;
  saleType: SaleType;
  quantity: number;
  addons?: Addon[];
  addGuestData?: AddGuestResponse;
  addOns?: string[];
  email?: string;
  handleCloseModal: () => void;
  closeModal: () => void;
  setStep: (step: ProcessOfflineSalesStep) => void;
  onSubmit: (e: OfflineSalesForm) => void;
}) => {
  const { t } = useTranslation();
  const methods = useFormContext<OfflineSalesForm>();
  const { handleSubmit } = methods;

  switch (step) {
    case STEP_SELECT_TYPE:
      return <SelectType onContinue={() => setStep(STEP_INFO)} offer={offer} />;
    case STEP_INFO:
      return (
        offer &&
        event &&
        (saleType === SINGLE_OFFLINE_SELL ? (
          <SummaryFormModalContent
            event={event}
            offer={offer}
            addons={addons ?? []}
            quantity={quantity}
            selectedAddons={addOns}
            showNominativeFields
            onContinue={handleSubmit(onSubmit)}
            handleClose={handleCloseModal}
            title={t('offers:fillInfoOfflineSalesTitle')}
            mainActionText={t('offers:confirmSaleButtonText')}
          />
        ) : (
          <SummaryFormMultiModalContent
            event={event}
            offer={offer}
            addons={addons ?? []}
            quantity={quantity}
            selectedAddons={addOns}
            showNominativeFields
            onContinue={handleSubmit(onSubmit)}
            handleClose={handleCloseModal}
            title={t('offers:fillInfoOfflineSalesTitle')}
            mainActionText={t('offers:confirmSaleButtonText')}
          />
        ))
      );
    case STEP_SUCCESS:
      return (
        offer &&
        addGuestData && (
          <SummarySuccessModalContent
            offer={offer}
            addons={addons ?? []}
            quantity={quantity}
            bookingLegacyId={addGuestData?.legacyId}
            onClickMainAction={() => setStep(STEP_SELECT_TYPE)}
            onClickSecondaryAction={() => createBookingLink(addGuestData.token)}
            secondaryActionIcon={IconDownload}
            secondaryActionText={t('offers:downloadPdf')}
            onClickClose={closeModal}
            title={t('offers:processOfflineSalesSuccessTitle')}
            headerDescription={
              email ? (
                <span className='text-secondary text-body-s'>
                  <Trans
                    i18nKey='offers:processEmailBookingSuccessDescription'
                    components={{ b: <b /> }}
                    values={{ email, bookingLegacyId: addGuestData.legacyId }}
                  />
                </span>
              ) : undefined
            }
            mainActionText={t('offers:buttonSaleMore')}
          />
        )
      );
    default:
      return null;
  }
};
